// libraries
import Vue from 'vue'

import { EventBus } from '@/helpers/eventBus'
import { setItem } from '@/helpers/storage-manager'

const resource = 'account'
const resourceName = 'cuenta'
const resourceNamePlural = 'cuentas'

const state = {
  loading_attrs: false,
  loading_views: false,
  account: {},
  admins: [],
  attrs: [],
  dashboard: [],
  user_attributes: [],
  user_attributes_values: [],
  languages: [],
  selected_language_id: undefined,
  bmessaging: [],
  bmessaging_requested: false,
  views: [],
  custom_views: [],
  account_views_last_update: null,
  updatingUserAttributes: false,
  cards: [],
  account_configuration: {},
  account_configuration_v2: {},
  showCallCounting: false,
  fromNumber: ''
}

state[resource + 's'] = []

// functions receive state
const getters = {
  defaultLanguageName(state) {
    const lan = state.languages.find((l) => l.language_id === 1)
    if (lan) {
      return lan.name || ''
    }

    return ''
  },
  defaultLocale(state) {
    const lan = state.languages.find((l) => l.language_id === 1)
    if (lan) {
      return lan.locale || ''
    }

    return ''
  },
  selectedLanguage(state) {
    return state.selected_language_id || 1
  },
  selectedLanguageName(state) {
    const lan = state.languages.find(
      (l) => l.language_id === (state.selected_language_id || 1)
    )
    if (lan) {
      return lan.name || ''
    }

    return ''
  },
  selectedLocale(state) {
    const lan = state.languages.find(
      (l) => l.language_id === (state.selected_language_id || 1)
    )
    if (lan) {
      return lan.locale || ''
    }

    return ''
  },
  currentAccount(state) {
    return state.account
  },
  loadingAttrs(state) {
    return state.loading_attrs
  },
  loadingViews(state) {
    return state.loading_views
  },
  loadAccounts(state) {
    return state.accounts
  },
  accountTimezone (state) {
    const {timezone} = state.account
    return timezone && timezone.length ? timezone : 'America/Mexico_City'
  },
  getShowCallCounting (state) {
    return state.showCallCounting
  },
  getFromNumber(state) {
    return state.fromNumber
  }
}

// functions receive context and params
const actions = {
  async all({ commit }, params) {
    try {
      if (!params.admin_id) {
        params.callback({ error: `Error obteniendo ${resourceNamePlural}.` })
      }

      commit('setItems', [])
      const res = await Vue.request.get(
        `admin/${params.admin_id}/${resource}s`,
        { params: params.params || {} }
      )
      if (res.data.success) {
        commit('setItems', res.data.resource)
        if (params.callback) {
          params.callback(res.data)
        }
      } else {
        if (params.callback) {
          params.callback({ error: `Error obteniendo ${resourceNamePlural}.` })
        }
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo ${resourceNamePlural}.`
        })
      }
      console.error(e)
    }
  },
  async get({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}`,
        { params: params.params || {} }
      )
      commit('setAccount', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error obteniendo ${resourceName}.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async create({ commit }, params) {
    try {
      const res = await Vue.request.post(`${resource}`, params)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error creando ${resourceName}.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: `Ocurrió un error creando ${resourceName}.` })
      }
      console.error(e)
    }
  },
  async update({ commit }, params) {
    try {
      const res = await Vue.request.patch(
        `${resource}/${params[resource + '_id']}`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error actualizando ${resourceName}.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error actualizando ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async destroy({ commit }, params) {
    try {
      const res = await Vue.request.delete(
        `${resource}/${params[resource + '_id']}`,
        { data: params }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error eliminando ${resourceName}.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error eliminando ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async admins({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/admins`,
        { params: params.params || {} }
      )
      commit('setAdmins', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo admins de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo admins de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async attributes({ commit }, params) {
    state.loading_attrs = true
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/all_user_attributes`,
        { params: params.params || {} }
      )
      commit('setAttrs', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo variables de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo variables de ${resourceName}.`
        })
      }
      console.error(e)
    }
    state.loading_attrs = false
  },
  async userAttributes({ commit, state }, params) {
    try {
      if (
        state.updatingUserAttributes ||
        !canUpdateUserAttributes(state.lastUserAttributesUpdate)
      ) {
        if (params.callback) {
          params.callback({ success: true })
        }

        return
      } else {
        commit('setLastUserAttributesUpdate')
      }

      commit('toggleUpdatingUserAttributes', true)
      commit('setUserAttributes', [])
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/user_attributes`,
        { params: params.params || {} }
      )

      commit('toggleUpdatingUserAttributes')
      if (res.data.success && params.callback) {
        commit('setUserAttributes', res.data.resource)
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo variables de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo variables de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async properties({ commit, state }, params) {
    try {
      if (
        state.updatingUserAttributes ||
        !canUpdateUserAttributes(state.lastUserAttributesUpdate)
      ) {
        if (params.callback) {
          params.callback({ success: true })
        }

        return
      } else {
        commit('setLastUserAttributesUpdate')
      }

      commit('toggleUpdatingUserAttributes', true)
      commit('setUserAttributes', [])
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/properties`,
        { params: params.params || {} }
      )

      commit('toggleUpdatingUserAttributes')
      if (res.data.success && params.callback) {
        commit('setUserAttributes', res.data.resource)
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo variables de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo variables de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async userAttributesValues({ commit }, params) {
    try {
      commit('setUserAttributesValues', [])
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/user_attributes/${
          params.user_id
        }`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        commit('setUserAttributesValues', res.data.resource)
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: 'Error obteniendo variables de usuario.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: 'Ocurrió un error obteniendo variables de usuario.'
        })
      }
      console.error(e)
    }
  },
  // GET ACTIVE USERS DATA
  async dashboard({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/dashboard`,
        { params: params.params || {} }
      )
      commit('setdashboard', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo info de usuarios de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo info de usuarios de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async billing({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/billing`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo facturación de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo facturación de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async billings({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/billings`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo facturación de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo facturación de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  // CONTRACT iFrame
  async iFrame({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/iFrame`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo contrato de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo contrato de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async parameters({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/parameters`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo variables de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo variables de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async updatePlatformSubscription({ commit }, params) {
    try {
      const res = await Vue.request.post(
        `${resource}/${params[resource + '_id']}/platform/${
          params.platform_id
        }`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else {
        params.callback({ error: 'Error actualizando plataforma.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error actualizando plataforma.' })
      }
      console.error(e)
    }
  },
  async updatePlatformConfiguration({ commit }, params) {
    try {
      const res = await Vue.request.patch(
        `${resource}/${params[resource + '_id']}/platform/${
          params.platform_id
        }`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else {
        params.callback({ error: 'Error actualizando plataforma.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error actualizando plataforma.' })
      }
      console.error(e)
    }
  },
  async desconnectPlatform({ commit }, params) {
    try {
      const res = await Vue.request.delete(
        `${resource}/${params[resource + '_id']}/platform/${
          params.platform_id
        }`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else {
        params.callback({ error: 'Error actualizando plataforma.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error actualizando plataforma.' })
      }
      console.error(e)
    }
  },
  async languages({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/languages`,
        { params: params.params || {} }
      )
      commit('setLanguages', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo idiomas de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo idiomas de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async bmessaging({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/bmessaging`,
        { params: params.params || {} }
      )
      commit('setBMessagingRequested')
      if (res.data.success) {
        commit('setBMessagingData', res.data.resource)
        if (params.callback) params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error obteniendo ${resourceName}.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async paymentLink({ commit }, params) {
    try {
      const res = await Vue.request.post(
        `${resource}/${params[resource + '_id']}/sendPaymentLink`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: res.data.error || 'Error generando liga de pago.'
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error generando liga de pago.' })
      }
      console.error(e)
    }
  },
  async views({ commit, state }, params) {
    state.loading_views = true
    try {
      if (state.account_views_last_update) {
        const current = new Date().getTime()
        if (current - state.account_views_last_update < 1000 * 60 * 1) {
          if (params.callback) {
            params.callback({
              success: true,
              resource: state.views.concat(state.custom_views)
            })
          }

          return
        }
      }

      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/views`,
        { params: params.params || {} }
      )
      if (res.data.success) {
        commit(
          'setViews',
          res.data.resource.filter((v) => !v.custom_view_id)
        )
        commit(
          'setCustomViews',
          res.data.resource
            .filter((v) => v.custom_view_id)
            .map((v) => {
              v.view_id = v.custom_view_id
              return v
            })
        )
        commit('setLastViewsUpdate')

        if (params.callback) {
          params.callback(res.data)
        }
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo vistas web de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo vistas web de ${resourceName}.`
        })
      }
      console.error(e)
    }
    state.loading_views = false
  },
  async updateAccessToken({ commit }, params) {
    try {
      const res = await Vue.request.patch(
        `${params.platform}/${params.model_id}`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else {
        params.callback({ error: 'Error actualizando plataforma.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error actualizando plataforma.' })
      }
      console.error(e)
    }
  },
  async praga({ commit }, params) {
    try {
      const res = await Vue.request.post(
        `v2/${resource}/${params[resource + '_id']}/praga`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: res.data.error || 'Error generando cuenta de cobros.'
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: 'Ocurrió un error generando cuenta de cobros.'
        })
      }
      console.error(e)
    }
  },
  async getPraga({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `v2/${resource}/${params[resource + '_id']}/praga`,
        params
      )
      if (res.status === 200) {
        params.callback(res.data)
      } else if (res.status === 404) {
        params.callback(null)
      } else {
        params.callback({ error: 'Error obteniendo cuenta de cobros.' })
      }
    } catch (e) {
      if (e.response.status !== 404 && params.callback) {
        params.callback({
          error: 'Ocurrió un error obteniendo cuenta de cobros.'
        })
      }
    }
  },
  async pragaStatus({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `v2/${resource}/${params[resource + '_id']}/pragaStatus`,
        params
      )
      if (res.status === 200) {
        params.callback(res.data)
      } else if (res.status === 404) {
        params.callback(null)
      } else {
        params.callback({ error: 'Error obteniendo cuenta de cobros.' })
      }
    } catch (e) {
      if (e.response.status !== 404 && params.callback) {
        params.callback({
          error: 'Ocurrió un error obteniendo cuenta de cobros.'
        })
      }
    }
  },
  async cards({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/cards`,
        { params: params.params || {} }
      )
      commit('setCards')
      if (res.data.success) {
        commit('setCards', res.data.resource)
        if (params.callback) params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: 'Error obteniendo tarjetas.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error obteniendo tarjetas.' })
      }
      console.error(e)
    }
  },
  async create_package({ commit }, params) {
    try {
      const res = await Vue.request.post(
        `${resource}/${params[resource + '_id']}/package`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error creando ${resourceName} de konverza.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error creando ${resourceName} de konverza.`
        })
      }
      console.error(e)
    }
  },
  async validateUsername({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `username/${params.username}/validate`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: 'Error validando username.' })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: 'Ocurrió un error validando username.' })
      }
      console.error(e)
    }
  },
  async get_configuration({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/configuration`,
        { params: params.params || {} }
      )
      commit('setAccountConfig', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo configuración de la ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo configuración de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async get_configuration_v2({ commit }, params) {
    try {
      const res = await Vue.request.get(`configuration/v2/${params[resource + '_id']}`)
      commit('setAccountConfigV2', res.data)
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo configuración de la ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo configuración de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async update_configuration_v2({ commit }, params) {
    try {
      const res = await Vue.request.patch(`configuration/v2/${params[resource + '_id']}`, params)

      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error actualizando configuración de la ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error actualizando configuración de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async update_configuration({ commit }, params) {
    try {
      const res = await Vue.request.patch(
        `${resource}/${params[resource + '_id']}/configuration`,
        params
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error actualizando configuración de la ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error actualizando configuración de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async status({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/status`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else {
        if (params.callback) {
          params.callback({
            error: `Error obteniendo estatus de ${resourceName}.`
          })
        }
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo estatus de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async importantBlocks({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/important_blocks`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo bloques de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo bloques de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async conversation_metrics({ commit }, params) {
    try {
      const res = await Vue.request_kpis.get(`${params[resource + '_id']}`, {
        params: params.params || {}
      })
      const { data } = res
      const { success = true, result } = data
      if (!success) throw new Error(result)
      params.callback(data)
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo métricas de conversación de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async chat_activity({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/chat_activity`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo métricas de ecommerce de la ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo métricas de ecommerce de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async messages_per_hour({ commit }, params) {
    try {
      const res = await Vue.request_kpis.get(
        `${params[resource + '_id']}/messagesPerHour`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo mensajes por hora de la ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo mensajes por hora de la ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async plugins({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/plugins`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo plugins de ${resourceName}.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo plugins de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async stripe({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/stripe`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo ${resourceName} de Stripe.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo ${resourceName} de Stripe.`
        })
      }
      console.error(e)
    }
  },
  async paypal({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/paypals`,
        { params: params.params || {} }
      )
      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({
          error: `Error obteniendo ${resourceName} de Paypal.`
        })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo ${resourceName} de Paypal.`
        })
      }
      console.error(e)
    }
  },
  async phones_status({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `whatsapp/${params.waba_id}/phones_status`
      )
      if (params.callback) {
        params.callback(res.data)
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo estatus de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async phone_stats({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `whatsapp/${params.waba_id}/phone_stats/${params.phone_number}`,
        { params: params.params || {} }
      )
      if (params.callback) {
        params.callback(res.data)
      }
    } catch (e) {
      if (params.callback) {
        params.callback({
          error: `Ocurrió un error obteniendo estatus de ${resourceName}.`
        })
      }
      console.error(e)
    }
  },
  async orderPayment(
    { commit },
    { callback = (data) => {}, data = {}, accountId }
  ) {
    try {
      if (!accountId) throw new Error('No se encontró la cuenta')
      const res = await Vue.request.post(
        `${resource}/${accountId}/inbox_payment`,
        data
      )
      if (!res.data.success) throw new Error(res.data.error)
      callback(res.data)
    } catch (error) {
      console.error(error)
      const errorMessage = { error: error.message }
      callback(errorMessage)
    }
  },
  async getAdminsActivity({ commit }, { accountId, params, callback }) {
    try {
      const res = await Vue.request_cases.get(`admins/${accountId}/activity`, {
        params
      })
      if (res.status !== 200) throw new Error(res.data.error)
      callback(res.data)
    } catch (error) {
      console.error(error)
      const errorMessage = { error: error.message }
      callback(errorMessage)
    }
  },
  async getAdminsAnswers({ commit }, { accountId, params, callback }) {
    try {
      const res = await Vue.request_cases.get(`admins/${accountId}/cases/answers`, {
        params
      })
      if (res.status !== 200) throw new Error(res.data.error)
      callback(res.data)
    } catch (error) {
      console.error(error)
      const errorMessage = { error: error.message }
      callback(errorMessage)
    }
  },
  async getAdminAgents({ commit }, { accountId, params, callback }) {
    try {
      const res = await Vue.request.get(`${resource}/${accountId}/dialers`)

      if (res.status !== 200) throw new Error(res.data.error)

      callback(res.data)
    } catch (error) {
      console.error(error)

      const errorMessage = { error: error.message }

      callback(errorMessage)
    }
  },
  async getHistoryCall({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/call/history`,
        { params: params.params || {} }
      )

      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error obteniendo ${resourceName}.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: `Ocurrió un error obteniendo ${resourceName}.` })
      }
      console.error(e)
    }
  },
  async getCaseIdFromCall({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/call/${params.call_id}/case`,
        { params: params.params || {} }
      )

      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error obteniendo case id.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: `Ocurrió un error obteniendo case id.` })
      }
      console.error(e)
    }
  },
  async getNumbers({ commit }, params) {
    try {
      const res = await Vue.request.get(
        `${resource}/${params[resource + '_id']}/numbers`
      )
      if(res.data.resource.length) {
        commit('setFromNumber', `+${res.data.resource[0].phone_number}`)
      }

      if (res.data.success && params.callback) {
        params.callback(res.data)
      } else if (params.callback) {
        params.callback({ error: `Error obteniendo case id.` })
      }
    } catch (e) {
      if (params.callback) {
        params.callback({ error: `Ocurrió un error obteniendo case id.` })
      }
      console.error(e)
    }
  },
}

// functions receive state
const mutations = {
  setFromNumber(state, data) {
    state.fromNumber = data
  },
  setShowCallCounting(state, boolean) {
    state.showCallCounting = boolean
  },
  setItems(state, items) {
    state[resource + 's'] = items
  },
  setAccount(state, data) {
    if (data.success) {
      state.account = data.resource
    } else {
      state.account = {}
    }
  },
  setAdmins(state, data) {
    if (data.success) {
      state.admins = data.resource.map((el) => {
        delete el.password
        return el
      })
    } else {
      state.admins = []
    }
  },
  pushAdmin(state, admin) {
    state.admins.push(admin)
  },
  updateAdmin(state, admin) {
    const indexOfAdminState = state.admins.findIndex(
      (firstAdmin) => firstAdmin.admin_id === admin.admin_id
    )

    if (indexOfAdminState >= 0) {
      state.admins.splice(indexOfAdminState, 1, admin)
    }
  },
  updateAdminAttribute(state, data) {
    const admin = state.admins.find(
      (firstAdmin) => firstAdmin.admin_id === data.admin_id
    )

    if (admin) {
      admin[data.attribute] = data.value
    }
  },
  removeAdmin(state, admin) {
    const index = state.admins.findIndex((a) => a.admin_id === admin.admin_id)
    if (index >= 0) {
      state.admins.splice(index, 1)
    }
  },
  setAttrs(state, data) {
    if (data.success) {
      state.attrs = data.resource
    } else {
      state.attrs = []
    }
  },
  setdashboard(state, data) {
    if (data.success) {
      state.dashboard = data.resource
    } else {
      state.dashboard = []
    }
  },
  setUserAttributes(state, items) {
    state.user_attributes = items
  },
  setUserAttributesValues(state, items) {
    state.user_attributes_values = items
  },
  setLanguages(state, data) {
    if (data.success) {
      state.languages = data.resource
    } else {
      state.languages = []
    }
  },
  changeLanguage(state, languageId) {
    state.selected_language_id = isNaN(languageId) ? 1 : languageId
    EventBus.$emit('language:change', state.selected_language_id)
    setItem('selectedLanguage', state.selected_language_id)
  },
  setBMessagingData(state, data) {
    state.bmessaging = data
  },
  setBMessagingRequested(state) {
    state.bmessaging_requested = true
  },
  setViews(state, views) {
    state.views = views
  },
  setCustomViews(state, views) {
    state.custom_views = views
  },
  setLastViewsUpdate(state) {
    state.account_views_last_update = new Date().getTime()
  },
  toggleUpdatingUserAttributes(state, isUpdating) {
    state.updatingUserAttributes = isUpdating !== undefined ? isUpdating : false
  },
  setLastUserAttributesUpdate(state) {
    state.lastFlowsUpdate = Vue.prototype.$moment()
  },
  setCards(state, data) {
    state.cards = data || []
  },
  updateAccountAttribute(state, data) {
    try {
      state[resource][data.attribute] = data.value
    } catch (e) {}
  },
  setAccountConfig(state, data) {
    if (data.success) {
      state.account_configuration = data.resource
    } else {
      state.account_configuration = {}
    }
  },
  setAccountConfigV2(state, data) {
      state.account_configuration_v2 = data.success ? data.resource : {}
    
  }
}

const canUpdateUserAttributes = function (lastUserAttributesUpdate) {
  if (lastUserAttributesUpdate) {
    const ellapsedMinutes = Vue.prototype
      .$moment()
      .diff(lastUserAttributesUpdate, 'minutes')
    return ellapsedMinutes >= 1
  }

  return true
}

export default {
  state,
  mutations,
  actions,
  getters
}
